import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';
import ModalLayer from './ModalLayer';
import ConfirmDialog from './ConfirmDialog';
import SearchableCategorySelect from './SearchableCategorySelect';
import { showToast } from './Toast';
import { compressTaskPhoto } from '../utils/taskPhotoFile';
import { useSignedStorageUrl } from '../shared/signStorageObject';
import {
  CONSTRUCTION_OPTIONS,
  EXTERIOR_OPTIONS,
  FOUNDATION_OPTIONS,
  ROOF_OPTIONS,
} from '../shared/homeProfile';
import {
  PROPERTY_TYPE_OPTIONS,
  deletePrompt,
  formToRecord,
  linkedTaskMessage,
  taskToForm,
} from '../shared/globalTaskForm';
import { FREQUENCY_OPTIONS, SEASON_OPTIONS, categoryChoices } from '../shared/taskCategories';
import {
  blankTask,
  deleteTemplateCopy,
  formToTemplateRecord,
  moveTask,
  taskError,
  templateToForm,
} from '../shared/starterTemplateForm';

function applyOnboardingFlag(list, saved, id) {
  const rows = Array.isArray(list) ? list.slice() : [];
  const index = rows.findIndex((template) => template && template.id === id);
  const next = Object.assign({}, index >= 0 ? rows[index] : {}, saved || {}, { id });
  const cleared = rows.map((template) => {
    if (template.id === id) return next;
    if (next.is_homeowner_onboarding && template.agent_id == null) {
      return Object.assign({}, template, { is_homeowner_onboarding: false });
    }
    return template;
  });
  if (index === -1) return [next].concat(cleared);
  return cleared;
}

const MATCH_GROUPS = [
  ['construction', 'Walls', CONSTRUCTION_OPTIONS],
  ['exterior', 'Outside', EXTERIOR_OPTIONS],
  ['foundation', 'Under the home', FOUNDATION_OPTIONS],
  ['roof', 'Roof', ROOF_OPTIONS],
];

function toggleValue(list, id) {
  const current = Array.isArray(list) ? list : [];
  if (current.indexOf(id) === -1) return current.concat(id);
  return current.filter((item) => item !== id);
}

export default function GlobalTasksAdmin({
  colors,
  narrow,
  logAdminAction,
  previewTasks,
  previewTemplates,
  previewCounts,
  startMode = '',
  startTaskId = '',
}) {
  const preview = Array.isArray(previewTasks);
  const [tasks, setTasks] = useState(preview ? previewTasks : []);
  const [templates, setTemplates] = useState(Array.isArray(previewTemplates) ? previewTemplates : []);
  const [counts, setCounts] = useState(previewCounts || {});
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const templateStart = startMode === 'templates' || startMode === 'template' || startMode === 'template-task' || startMode === 'template-delete';
  const [section, setSection] = useState(templateStart ? 'templates' : 'tasks');
  const [editor, setEditor] = useState(null);
  const [templateEditor, setTemplateEditor] = useState(null);
  const [taskDraft, setTaskDraft] = useState(null);
  const [templateDelete, setTemplateDelete] = useState(null);
  const [deleteStep, setDeleteStep] = useState('');
  const [deleteTarget, setDeleteTargetState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const previewUrl = useSignedStorageUrl('task-photos', editor && editor.form ? editor.form.image_url : '');

  useEffect(() => {
    if (preview) return undefined;
    let live = true;
    async function load() {
      const taskResult = await supabase
        .from('tasks')
        .select('*')
        .is('agent_id', null)
        .order('category', { ascending: true })
        .order('title', { ascending: true });
      if (!live) return;
      if (taskResult.error) {
        setError(taskResult.error.message || 'Could not load global tasks');
        return;
      }
      setTasks(taskResult.data || []);
      let templateResult = await supabase
        .from('project_templates')
        .select('id, name, description, category, frequency, tasks, is_global, agent_id, is_homeowner_onboarding')
        .is('agent_id', null)
        .order('name');
      if (templateResult.error && /category|frequency|is_homeowner_onboarding/i.test(templateResult.error.message || '')) {
        templateResult = await supabase
          .from('project_templates')
          .select('id, name, description, tasks, is_global, agent_id')
          .is('agent_id', null)
          .order('name');
      }
      if (!live) return;
      if (templateResult.error) {
        setError(templateResult.error.message || 'Could not load starter templates');
        return;
      }
      setTemplates(templateResult.data || []);
    }
    load();
    return () => {
      live = false;
    };
  }, [preview]);

  useEffect(() => {
    if (!startMode || editor || deleteStep) return;
    if (startMode === 'templates' || startMode === 'template' || startMode === 'template-task' || startMode === 'template-delete') return;
    const row = (startTaskId && tasks.find((task) => task.id === startTaskId))
      || tasks.find((task) => task && task.active !== false)
      || tasks[0];
    if (!row) return;
    if (startMode === 'delete') openDelete(row);
    else openEditor(row);
  }, [startMode, startTaskId, tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!startMode || templateEditor || taskDraft || templateDelete) return;
    if (startMode !== 'template' && startMode !== 'template-task' && startMode !== 'template-delete') return;
    const row = (startTaskId && templates.find((template) => template.id === startTaskId)) || templates[0];
    if (!row) return;
    if (startMode === 'template-delete') {
      setTemplateDelete(row);
      return;
    }
    const form = templateToForm(row);
    setTemplateEditor({ id: row.id, form });
    if (startMode === 'template-task' && form.tasks[0]) {
      setTaskDraft({ index: 0, form: { ...form.tasks[0], extra: { ...form.tasks[0].extra } } });
    }
  }, [startMode, startTaskId, templates, templateEditor, taskDraft, templateDelete]);

  const categories = useMemo(() => {
    const fromTemplates = (templates || []).flatMap((template) => (
      Array.isArray(template.tasks) ? template.tasks : []
    ));
    return categoryChoices(tasks.concat(fromTemplates));
  }, [tasks, templates]);
  const visible = tasks.filter((task) => {
    if (category && String(task.category || '') !== category) return false;
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    const haystack = `${task.title || ''} ${task.description || ''} ${task.category || ''}`.toLowerCase();
    return haystack.indexOf(needle) !== -1;
  });

  function knownCount(taskId) {
    if (!taskId) return 0;
    if (previewCounts && Object.prototype.hasOwnProperty.call(previewCounts, taskId)) {
      return Number(previewCounts[taskId]) || 0;
    }
    if (Object.prototype.hasOwnProperty.call(counts, taskId)) return Number(counts[taskId]) || 0;
    return null;
  }

  async function countFor(taskId) {
    const known = knownCount(taskId);
    if (known != null) return known;
    const result = await supabase
      .from('client_tasks')
      .select('id', { count: 'exact', head: true })
      .eq('task_id', taskId);
    if (result.error) throw result.error;
    const total = result.count || 0;
    setCounts((current) => ({ ...current, [taskId]: total }));
    return total;
  }

  async function openEditor(task) {
    const form = taskToForm(task);
    setError('');
    setEditor({ id: task && task.id, form, linked: null });
    if (!task || !task.id) {
      setEditor({ id: null, form, linked: 0 });
      return;
    }
    try {
      const linked = await countFor(task.id);
      setEditor({ id: task.id, form, linked });
    } catch (loadError) {
      setEditor({ id: task.id, form, linked: null });
      setError(loadError.message || 'Could not count homeowner tasks');
    }
  }

  function openNew() {
    setError('');
    setEditor({ id: null, form: taskToForm({ type: 'maintenance', priority: 'medium', active: true }), linked: 0 });
  }

  function patchForm(patch) {
    setEditor((current) => (current ? { ...current, form: { ...current.form, ...patch } } : current));
  }

  function patchMatch(patch) {
    setEditor((current) => {
      if (!current) return current;
      return { ...current, form: { ...current.form, match: { ...current.form.match, ...patch } } };
    });
  }

  async function remember(actionType, taskId, title, linked) {
    if (!logAdminAction) return;
    await logAdminAction(actionType, 'task', taskId, {
      title,
      linked: Number(linked) || 0,
    });
  }

  async function saveEditor() {
    if (!editor) return;
    const parsed = formToRecord(editor.form);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (preview) {
        const id = editor.id || `preview-${Date.now()}`;
        const next = { ...parsed.record, id };
        setTasks((current) => {
          const rest = current.filter((task) => task.id !== id);
          return [next].concat(rest);
        });
        setEditor(null);
        return;
      }
      if (editor.id) {
        const result = await supabase
          .from('tasks')
          .update(parsed.record)
          .eq('id', editor.id)
          .is('agent_id', null)
          .select('id')
          .maybeSingle();
        if (result.error) throw result.error;
        if (!result.data) throw new Error('Could not save this task');
        await remember('update_global_task', editor.id, parsed.record.title, editor.linked);
        setTasks((current) => current.map((task) => (task.id === editor.id ? { ...task, ...parsed.record } : task)));
      } else {
        const result = await supabase
          .from('tasks')
          .insert(parsed.record)
          .select('*')
          .single();
        if (result.error) throw result.error;
        await remember('create_global_task', result.data.id, parsed.record.title, 0);
        setTasks((current) => [result.data].concat(current));
      }
      setEditor(null);
      showToast('Task saved.');
    } catch (saveError) {
      setError(saveError.message || 'Could not save this task');
    } finally {
      setBusy(false);
    }
  }

  async function setActive(task, active) {
    if (!task || !task.id) return;
    setBusy(true);
    setError('');
    try {
      if (!preview) {
        const result = await supabase
          .from('tasks')
          .update({ active })
          .eq('id', task.id)
          .is('agent_id', null)
          .select('id')
          .maybeSingle();
        if (result.error) throw result.error;
        if (!result.data) throw new Error('Could not update this task');
        await remember(active ? 'activate_global_task' : 'deactivate_global_task', task.id, task.title, counts[task.id]);
      }
      setTasks((current) => current.map((row) => (row.id === task.id ? { ...row, active } : row)));
      setEditor((current) => (current && current.id === task.id ? { ...current, form: { ...current.form, active } } : current));
      setDeleteStep('');
      showToast(active ? 'Task is active.' : 'Task deactivated.');
    } catch (updateError) {
      setError(updateError.message || 'Could not update this task');
    } finally {
      setBusy(false);
    }
  }

  async function openDelete(task) {
    setError('');
    const known = knownCount(task.id);
    if (known != null) {
      setDeleteTargetState({ task, linked: known });
      setDeleteStep('choose');
      return;
    }
    try {
      const linked = await countFor(task.id);
      setCounts((current) => ({ ...current, [task.id]: linked }));
      setDeleteTargetState({ task, linked });
      setDeleteStep('choose');
    } catch (loadError) {
      setError(loadError.message || 'Could not count homeowner tasks');
    }
  }

  async function removeTask(confirmDelete) {
    const target = deleteTarget && deleteTarget.task;
    if (!target) return;
    setBusy(true);
    setError('');
    try {
      if (!preview) {
        const result = await supabase.rpc('delete_global_task', {
          p_task_id: target.id,
          p_confirm: confirmDelete === true,
        });
        if (result.error) throw result.error;
        if (result.data && result.data.needs_confirm) {
          setDeleteStep('confirm-delete');
          setDeleteTargetState({ task: target, linked: Number(result.data.linked) || deleteTarget.linked });
          return;
        }
      }
      setTasks((current) => current.filter((task) => task.id !== target.id));
      if (editor && editor.id === target.id) setEditor(null);
      setDeleteStep('');
      setDeleteTargetState(null);
      showToast('Task deleted.');
    } catch (deleteError) {
      setError(deleteError.message || 'Could not delete this task');
    } finally {
      setBusy(false);
    }
  }

  async function uploadPhoto(event) {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file || !editor || !editor.id) return;
    setBusy(true);
    try {
      let body = file;
      let extension = (file.name.split('.').pop() || 'jpg').toLowerCase();
      try {
        const prepared = await compressTaskPhoto(file);
        if (prepared && prepared.size > 0 && prepared.size < file.size) {
          body = prepared;
          extension = 'jpg';
        }
      } catch (prepareError) {
        body = file;
      }
      const path = `${editor.id}/${Date.now()}.${extension}`;
      const upload = await supabase.storage.from('task-photos').upload(path, body, {
        cacheControl: '3600',
        upsert: false,
        contentType: body.type || file.type || 'image/jpeg',
      });
      if (upload.error) throw upload.error;
      const { data: { publicUrl } } = supabase.storage.from('task-photos').getPublicUrl(path);
      patchForm({ image_url: publicUrl });
    } catch (uploadError) {
      setError(uploadError.message || 'Could not upload that photo');
    } finally {
      setBusy(false);
    }
  }

  function patchTemplate(patch) {
    setTemplateEditor((current) => (current ? { ...current, form: { ...current.form, ...patch } } : current));
  }

  function openTemplate(template) {
    setError('');
    setTaskDraft(null);
    setTemplateEditor({ id: template.id, form: templateToForm(template) });
  }

  function openNewTemplate() {
    setError('');
    setTaskDraft(null);
    setTemplateEditor({ id: null, form: templateToForm({ tasks: [] }) });
  }

  function openTaskDraft(index) {
    if (!templateEditor) return;
    const task = templateEditor.form.tasks[index];
    if (!task) return;
    setError('');
    setTaskDraft({ index, form: { ...task, extra: { ...(task.extra || {}) } } });
  }

  function openNewTask() {
    setError('');
    setTaskDraft({ index: null, form: blankTask() });
  }

  function patchTaskDraft(patch) {
    setTaskDraft((current) => (current ? { ...current, form: { ...current.form, ...patch } } : current));
  }

  function saveTaskDraft() {
    if (!taskDraft || !templateEditor) return;
    const problem = taskError(taskDraft.form);
    if (problem) {
      setError(problem);
      return;
    }
    setError('');
    const next = taskDraft.form;
    setTemplateEditor((current) => {
      if (!current) return current;
      const tasks = current.form.tasks.slice();
      if (taskDraft.index == null) tasks.push(next);
      else tasks[taskDraft.index] = next;
      return { ...current, form: { ...current.form, tasks } };
    });
    setTaskDraft(null);
  }

  function removeTaskDraft() {
    if (!taskDraft || taskDraft.index == null) {
      setTaskDraft(null);
      return;
    }
    setTemplateEditor((current) => {
      if (!current) return current;
      return {
        ...current,
        form: {
          ...current.form,
          tasks: current.form.tasks.filter((_, index) => index !== taskDraft.index),
        },
      };
    });
    setTaskDraft(null);
  }

  function reorderTemplateTask(index, direction) {
    setTemplateEditor((current) => {
      if (!current) return current;
      return { ...current, form: { ...current.form, tasks: moveTask(current.form.tasks, index, direction) } };
    });
  }

  async function rememberTemplate(actionType, templateId, name, taskCount) {
    if (!logAdminAction) return;
    await logAdminAction(actionType, 'project_template', templateId, {
      name,
      task_count: Number(taskCount) || 0,
    });
  }

  async function saveTemplate() {
    if (!templateEditor) return;
    const parsed = formToTemplateRecord(templateEditor.form);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (preview) {
        const id = templateEditor.id || `preview-${Date.now()}`;
        const next = { ...parsed.record, id };
        setTemplates((current) => applyOnboardingFlag(current, next, id));
        setTemplateEditor(null);
        showToast('Template saved.');
        return;
      }
      if (templateEditor.id) {
        const result = await supabase
          .from('project_templates')
          .update({ ...parsed.record, updated_at: new Date().toISOString() })
          .eq('id', templateEditor.id)
          .is('agent_id', null)
          .select('id')
          .maybeSingle();
        if (result.error) throw result.error;
        if (!result.data) throw new Error('Could not save this template');
        await rememberTemplate('update_global_template', templateEditor.id, parsed.record.name, parsed.record.tasks.length);
        setTemplates((current) => applyOnboardingFlag(current, parsed.record, templateEditor.id));
      } else {
        const result = await supabase
          .from('project_templates')
          .insert(parsed.record)
          .select('*')
          .single();
        if (result.error) throw result.error;
        await rememberTemplate('create_global_template', result.data.id, parsed.record.name, parsed.record.tasks.length);
        setTemplates((current) => applyOnboardingFlag(current, result.data, result.data.id));
      }
      setTemplateEditor(null);
      showToast('Template saved.');
    } catch (saveError) {
      setError(saveError.message || 'Could not save this template');
    } finally {
      setBusy(false);
    }
  }

  async function removeTemplate() {
    const target = templateDelete;
    if (!target) return;
    setBusy(true);
    setError('');
    try {
      if (!preview) {
        const result = await supabase.rpc('delete_global_template', { p_template_id: target.id });
        if (result.error) throw result.error;
      }
      setTemplates((current) => current.filter((template) => template.id !== target.id));
      if (templateEditor && templateEditor.id === target.id) setTemplateEditor(null);
      setTemplateDelete(null);
      showToast('Template deleted.');
    } catch (deleteError) {
      setError(deleteError.message || 'Could not delete this template');
    } finally {
      setBusy(false);
    }
  }

  const prompt = deleteTarget ? deletePrompt(deleteTarget.linked) : null;
  const templatePrompt = templateDelete ? deleteTemplateCopy(templateDelete.name) : null;
  const field = {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '44px',
    fontSize: '16px',
    padding: '0.65rem 0.75rem',
    borderRadius: '0.75rem',
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
  };
  const labelStyle = { display: 'block', margin: '0.9rem 0 0.3rem', fontWeight: 650, color: colors.textSecondary, fontSize: '0.85rem' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: narrow ? 'stretch' : 'center', flexDirection: narrow ? 'column' : 'row', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0, color: colors.text, fontSize: narrow ? '1.25rem' : '1.5rem' }}>Pacarion Starter Tasks</h2>
          <p style={{ margin: '0.35rem 0 0', color: colors.textSecondary, lineHeight: 1.45 }}>
            {section === 'templates'
              ? 'Checklists agents can start from. A change here does not change a checklist an agent already copied. No homeowner names are shown here.'
              : 'Shared with every agent. A saved change shows on the homeowner tasks linked to it. No homeowner names are shown here.'}
          </p>
        </div>
        {section === 'tasks' ? (
          <button type="button" onClick={openNew} style={primaryButton(colors)}>New task</button>
        ) : (
          <button type="button" onClick={openNewTemplate} style={primaryButton(colors)}>New template</button>
        )}
      </div>
      <div role="tablist" aria-label="Pacarion Starter Tasks" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' }}>
        {[['tasks', 'Tasks'], ['templates', 'Pacarion Starter Templates']].map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={section === id}
            onClick={() => setSection(id)}
            style={segmentButton(colors, section === id)}
          >
            {label}
          </button>
        ))}
      </div>
      {error ? <p role="alert" style={{ color: '#b91c1c' }}>{error}</p> : null}

      {section === 'templates' ? (
        <TemplateList
          templates={templates}
          colors={colors}
          onEdit={openTemplate}
          onDelete={setTemplateDelete}
        />
      ) : (
        <>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search tasks"
            aria-label="Search tasks"
            style={{ ...field, marginBottom: '0.75rem' }}
          />
          <SearchableCategorySelect
            id="global-task-category"
            label="Category"
            value={category}
            onChange={setCategory}
            options={categories}
            emptyLabel="All categories"
            colors={colors}
          />
          <p style={{ margin: '0 0 0.75rem', color: colors.textSecondary }}>{visible.length} tasks</p>
          {visible.length === 0 ? (
            <p style={{ color: colors.text }}>No tasks match.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {visible.map((task) => (
                <button
                  key={task.id}
                  type="button"
                  onClick={() => openEditor(task)}
                  style={{
                    textAlign: 'left',
                    minHeight: '52px',
                    padding: '0.75rem 1rem',
                    borderRadius: '0.9rem',
                    border: `1px solid ${colors.border}`,
                    background: colors.surface,
                    color: colors.text,
                    opacity: task.active === false ? 0.72 : 1,
                  }}
                >
                  <span style={{ display: 'block', fontWeight: 650 }}>{task.title}</span>
                  <span style={{ display: 'block', marginTop: '0.2rem', color: colors.textSecondary, fontSize: '0.9rem' }}>
                    {task.category} · {task.frequency}
                    {task.active === false ? ' · Inactive' : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {editor ? (
        <ModalLayer style={overlayStyle} onClick={() => setEditor(null)}>
          <div
            className="app-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="global-task-editor-title"
            onClick={(event) => event.stopPropagation()}
            style={{ background: colors.surface, color: colors.text, width: 'min(100%, 40rem)', maxHeight: '100%', overflow: 'auto', borderRadius: '1rem', padding: '1rem', boxSizing: 'border-box' }}
          >
            <h3 id="global-task-editor-title" style={{ marginTop: 0 }}>{editor.id ? 'Edit task' : 'New task'}</h3>
            <p style={{ color: colors.textSecondary, lineHeight: 1.45 }}>
              {editor.linked == null ? 'Checking how many homeowner tasks use this.' : linkedTaskMessage(editor.linked)}
            </p>
            <label style={labelStyle} htmlFor="global-task-title">Title</label>
            <input id="global-task-title" value={editor.form.title} onChange={(event) => patchForm({ title: event.target.value })} style={field} />
            <label style={labelStyle} htmlFor="global-task-description">Description</label>
            <textarea id="global-task-description" value={editor.form.description} onChange={(event) => patchForm({ description: event.target.value })} style={{ ...field, minHeight: '7rem' }} />
            <label style={labelStyle} htmlFor="global-task-edit-category">Category</label>
            <input id="global-task-edit-category" list="global-task-categories" value={editor.form.category} onChange={(event) => patchForm({ category: event.target.value })} style={field} />
            <datalist id="global-task-categories">
              {categories.map((name) => <option key={name} value={name} />)}
            </datalist>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 10rem' }}>
                <label style={labelStyle} htmlFor="global-task-type">Type</label>
                <select id="global-task-type" value={editor.form.type} onChange={(event) => patchForm({ type: event.target.value })} style={field}>
                  <option value="maintenance">Maintenance</option>
                  <option value="financial">Financial</option>
                </select>
              </div>
              <div style={{ flex: '1 1 10rem' }}>
                <label style={labelStyle} htmlFor="global-task-frequency">Frequency</label>
                <input id="global-task-frequency" value={editor.form.frequency} onChange={(event) => patchForm({ frequency: event.target.value })} style={field} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 10rem' }}>
                <label style={labelStyle} htmlFor="global-task-priority">Priority</label>
                <select id="global-task-priority" value={editor.form.priority} onChange={(event) => patchForm({ priority: event.target.value })} style={field}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div style={{ flex: '1 1 10rem' }}>
                <label style={labelStyle} htmlFor="global-task-difficulty">Difficulty</label>
                <select id="global-task-difficulty" value={editor.form.difficulty_level} onChange={(event) => patchForm({ difficulty_level: event.target.value })} style={field}>
                  <option value="">Not set</option>
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
            </div>
            <label style={labelStyle} htmlFor="global-task-time">Estimated time</label>
            <input id="global-task-time" value={editor.form.estimated_time} onChange={(event) => patchForm({ estimated_time: event.target.value })} style={field} />
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', minHeight: '44px' }}>
              <input type="checkbox" checked={editor.form.active !== false} onChange={(event) => patchForm({ active: event.target.checked })} />
              Active
            </label>
            <label style={labelStyle} htmlFor="global-task-image">Image address</label>
            <input id="global-task-image" value={editor.form.image_url} onChange={(event) => patchForm({ image_url: event.target.value })} style={field} />
            {editor.id ? (
              <label style={labelStyle}>
                Upload a photo
                <input type="file" accept="image/jpeg,image/jpg,image/png,image/heic,image/heif" onChange={uploadPhoto} style={{ ...field, marginTop: '0.3rem' }} />
              </label>
            ) : (
              <p style={{ color: colors.textSecondary }}>Save the task first, then add a photo.</p>
            )}
            {previewUrl ? <img src={previewUrl} alt="" style={{ maxWidth: '100%', borderRadius: '0.75rem', marginTop: '0.5rem' }} /> : null}
            <label style={labelStyle} htmlFor="global-task-diy">DIY tips</label>
            <textarea id="global-task-diy" value={editor.form.diy_tips} onChange={(event) => patchForm({ diy_tips: event.target.value })} style={{ ...field, minHeight: '6rem' }} />
            <label style={labelStyle} htmlFor="global-task-video">YouTube link</label>
            <input id="global-task-video" value={editor.form.youtube_video_url} onChange={(event) => patchForm({ youtube_video_url: event.target.value })} style={field} />
            <label style={labelStyle} htmlFor="global-task-pro">When to call a pro</label>
            <textarea id="global-task-pro" value={editor.form.when_to_call_pro} onChange={(event) => patchForm({ when_to_call_pro: event.target.value })} style={{ ...field, minHeight: '5rem' }} />
            <h4 style={{ marginBottom: '0.25rem' }}>Which homes</h4>
            <p style={{ marginTop: 0, color: colors.textSecondary }}>Leave every home type unchecked to include all of them.</p>
            {PROPERTY_TYPE_OPTIONS.map((option) => (
              <label key={option.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minHeight: '44px' }}>
                <input
                  type="checkbox"
                  checked={editor.form.property_types.indexOf(option.id) !== -1}
                  onChange={() => patchForm({ property_types: toggleValue(editor.form.property_types, option.id) })}
                />
                {option.label}
              </label>
            ))}
            <label style={labelStyle} htmlFor="global-task-feature">Required feature</label>
            <input id="global-task-feature" value={editor.form.required_feature} onChange={(event) => patchForm({ required_feature: event.target.value })} style={field} />
            <label style={labelStyle} htmlFor="global-task-month">Month</label>
            <input id="global-task-month" value={editor.form.month_to_perform} onChange={(event) => patchForm({ month_to_perform: event.target.value })} style={field} />
            <details style={{ marginTop: '1rem' }}>
              <summary style={{ minHeight: '44px', cursor: 'pointer' }}>Home match</summary>
              <p style={{ color: colors.textSecondary }}>Optional. Use this when property type and required feature are not enough.</p>
              {MATCH_GROUPS.map(([key, title, options]) => (
                <fieldset key={key} style={{ border: `1px solid ${colors.border}`, borderRadius: '0.75rem', margin: '0 0 0.75rem' }}>
                  <legend>{title}</legend>
                  {options.filter((option) => option.id !== 'not_sure').map((option) => (
                    <label key={option.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', minHeight: '44px' }}>
                      <input
                        type="checkbox"
                        checked={(editor.form.match[key] || []).indexOf(option.id) !== -1}
                        onChange={() => patchMatch({ [key]: toggleValue(editor.form.match[key], option.id) })}
                      />
                      {option.label}
                    </label>
                  ))}
                </fieldset>
              ))}
              <label style={labelStyle} htmlFor="global-task-hvac-index">Air conditioner number</label>
              <input id="global-task-hvac-index" inputMode="numeric" value={editor.form.match.hvacIndex} onChange={(event) => patchMatch({ hvacIndex: event.target.value })} style={field} />
              <label style={labelStyle} htmlFor="global-task-hvac-min">At least this many air conditioners</label>
              <input id="global-task-hvac-min" inputMode="numeric" value={editor.form.match.hvacMin} onChange={(event) => patchMatch({ hvacMin: event.target.value })} style={field} />
              <label style={labelStyle} htmlFor="global-task-hvac-max">At most this many air conditioners</label>
              <input id="global-task-hvac-max" inputMode="numeric" value={editor.form.match.hvacMax} onChange={(event) => patchMatch({ hvacMax: event.target.value })} style={field} />
              <label style={labelStyle} htmlFor="global-task-roof-min">Roof age, at least (years)</label>
              <input id="global-task-roof-min" inputMode="numeric" value={editor.form.match.roofAgeMin} onChange={(event) => patchMatch({ roofAgeMin: event.target.value })} style={field} />
              <label style={labelStyle} htmlFor="global-task-roof-max">Roof age, at most (years)</label>
              <input id="global-task-roof-max" inputMode="numeric" value={editor.form.match.roofAgeMax} onChange={(event) => patchMatch({ roofAgeMax: event.target.value })} style={field} />
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', minHeight: '44px' }}>
                <input type="checkbox" checked={editor.form.match.whenRoofAgeUnknown} onChange={(event) => patchMatch({ whenRoofAgeUnknown: event.target.checked })} />
                Include homes that do not have a roof year
              </label>
              <label style={labelStyle} htmlFor="global-task-match">Match</label>
              <select id="global-task-match" value={editor.form.match.match} onChange={(event) => patchMatch({ match: event.target.value })} style={field}>
                <option value="">All of these</option>
                <option value="all">All of these</option>
                <option value="any">Any of these</option>
              </select>
              <label style={labelStyle} htmlFor="global-task-key">Match key</label>
              <input id="global-task-key" value={editor.form.match.key} onChange={(event) => patchMatch({ key: event.target.value })} style={field} />
            </details>
            {error ? <p role="alert" style={{ color: '#b91c1c' }}>{error}</p> : null}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
              <button type="button" onClick={saveEditor} disabled={busy} style={primaryButton(colors)}>Save</button>
              {editor.id ? (
                <button type="button" onClick={(event) => { event.stopPropagation(); openDelete(tasks.find((task) => task.id === editor.id) || { id: editor.id, title: editor.form.title }); }} disabled={busy} style={quietButton(colors)}>
                  Delete
                </button>
              ) : null}
              <button type="button" onClick={() => setEditor(null)} style={quietButton(colors)}>Close</button>
            </div>
          </div>
        </ModalLayer>
      ) : null}

      {prompt && deleteStep === 'choose' ? (
        <ConfirmDialog
          open
          title={prompt.title}
          confirmLabel={prompt.confirmLabel}
          cancelLabel="Cancel"
          busy={busy}
          onCancel={() => setDeleteStep('')}
          onConfirm={() => {
            if (prompt.needsDeactivate) setActive(deleteTarget.task, false);
            else removeTask(false);
          }}
        >
          <p style={{ margin: '0 0 1rem', lineHeight: 1.5 }}>{prompt.message}</p>
          {prompt.needsDeactivate ? (
            <button type="button" onClick={() => setDeleteStep('confirm-delete')} style={{ ...quietButton(colors), marginBottom: '0.75rem' }}>
              Delete anyway
            </button>
          ) : null}
        </ConfirmDialog>
      ) : null}
      {prompt && deleteStep === 'confirm-delete' ? (
        <ConfirmDialog
          open
          title="Delete this task?"
          message={prompt.deleteMessage}
          confirmLabel="Delete"
          cancelLabel="Back"
          busy={busy}
          onCancel={() => setDeleteStep('choose')}
          onConfirm={() => removeTask(true)}
        />
      ) : null}

      {templateEditor ? (
        <TemplateEditor
          editor={templateEditor}
          colors={colors}
          field={field}
          labelStyle={labelStyle}
          categories={categories}
          busy={busy}
          error={error}
          onPatch={patchTemplate}
          onReorder={reorderTemplateTask}
          onEditTask={openTaskDraft}
          onAddTask={openNewTask}
          onSave={saveTemplate}
          onDelete={() => {
            const current = templates.find((template) => template.id === templateEditor.id) || {
              id: templateEditor.id,
              name: templateEditor.form.name,
            };
            setTemplateDelete(current);
          }}
          onClose={() => { setTemplateEditor(null); setError(''); }}
        />
      ) : null}

      {taskDraft ? (
        <TaskDraftEditor
          draft={taskDraft}
          colors={colors}
          field={field}
          labelStyle={labelStyle}
          categories={categories}
          error={error}
          onPatch={patchTaskDraft}
          onSave={saveTaskDraft}
          onRemove={removeTaskDraft}
          onClose={() => { setTaskDraft(null); setError(''); }}
        />
      ) : null}

      {templatePrompt ? (
        <ConfirmDialog
          open
          title={templatePrompt.title}
          message={templatePrompt.message}
          confirmLabel={templatePrompt.confirmLabel}
          cancelLabel="Cancel"
          busy={busy}
          onCancel={() => setTemplateDelete(null)}
          onConfirm={removeTemplate}
        />
      ) : null}
    </div>
  );
}

function TemplateList({ templates, colors, onEdit, onDelete }) {
  const rows = (templates || []).filter((template) => template && template.agent_id == null);
  return (
    <div>
      {rows.length === 0 ? <p style={{ color: colors.text }}>No Pacarion Starter Templates.</p> : null}
      {rows.map((template) => {
        const items = Array.isArray(template.tasks) ? template.tasks : [];
        const meta = [template.category, template.frequency].filter(Boolean).join(' · ');
        return (
          <article key={template.id} style={{ border: `1px solid ${colors.border}`, borderRadius: '0.9rem', padding: '0.9rem 1rem', marginBottom: '0.75rem', background: colors.surface }}>
            <h3 style={{ margin: '0 0 0.25rem' }}>{template.name}</h3>
            {meta ? <p style={{ margin: '0 0 0.35rem', color: colors.textSecondary }}>{meta}</p> : null}
            {template.description ? <p style={{ margin: '0 0 0.5rem', color: colors.textSecondary }}>{template.description}</p> : null}
            {template.is_homeowner_onboarding ? (
              <p style={{ margin: '0 0 0.5rem', color: colors.text, fontWeight: 650 }}>New homeowner onboarding</p>
            ) : null}
            <p style={{ margin: '0 0 0.5rem', color: colors.textSecondary }}>{items.length} tasks</p>
            <ol style={{ margin: '0 0 0.75rem', paddingLeft: '1.25rem' }}>
              {items.map((task, index) => (
                <li key={task.id || index}>{task.title}</li>
              ))}
            </ol>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button type="button" onClick={() => onEdit(template)} style={quietButton(colors)} aria-label={`Edit ${template.name}`}>
                Edit
              </button>
              <button type="button" onClick={() => onDelete(template)} style={quietButton(colors)} aria-label={`Delete ${template.name}`}>
                Delete
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function FrequencyField({ id, value, onChange, field, labelStyle }) {
  const known = FREQUENCY_OPTIONS.concat(SEASON_OPTIONS);
  const extra = value && known.indexOf(value) === -1 ? value : '';
  return (
    <>
      <label style={labelStyle} htmlFor={id}>Frequency or season</label>
      <select id={id} value={value || ''} onChange={(event) => onChange(event.target.value)} style={field}>
        <option value="">Choose</option>
        {extra ? <option value={extra}>{extra}</option> : null}
        <optgroup label="How often">
          {FREQUENCY_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
        </optgroup>
        <optgroup label="Season">
          {SEASON_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
        </optgroup>
      </select>
    </>
  );
}

function TemplateEditor({
  editor,
  colors,
  field,
  labelStyle,
  categories,
  busy,
  error,
  onPatch,
  onReorder,
  onEditTask,
  onAddTask,
  onSave,
  onDelete,
  onClose,
}) {
  const tasks = editor.form.tasks || [];
  return (
    <ModalLayer style={sheetOverlayStyle} onClick={onClose}>
      <div
        className="app-modal-card starter-template-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="starter-template-editor-title"
        onClick={(event) => event.stopPropagation()}
        style={{ background: colors.surface, color: colors.text, width: 'min(100%, 40rem)', borderRadius: '1rem', boxSizing: 'border-box' }}
      >
        <div className="app-modal-header" style={{ background: colors.surface }}>
          <h3 id="starter-template-editor-title">{editor.id ? 'Edit template' : 'New template'}</h3>
        </div>
        <div className="starter-template-sheet-body">
          <label style={labelStyle} htmlFor="starter-template-name">Name</label>
          <input id="starter-template-name" value={editor.form.name} onChange={(event) => onPatch({ name: event.target.value })} style={field} />
          <label htmlFor="starter-template-onboarding" style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginTop: '0.9rem', fontWeight: 650, color: colors.text }}>
            <input
              id="starter-template-onboarding"
              type="checkbox"
              checked={editor.form.isHomeownerOnboarding === true}
              onChange={(event) => onPatch({ isHomeownerOnboarding: event.target.checked })}
              style={{ width: '1.15rem', height: '1.15rem', margin: 0 }}
            />
            New homeowner onboarding
          </label>
          <p id="starter-template-onboarding-help" style={{ margin: '0.35rem 0 0', color: colors.textSecondary, lineHeight: 1.45 }}>
            New homeowner accounts get a project from this template. Only one starter template can be marked.
          </p>
          <SearchableCategorySelect
            id="starter-template-category"
            label="Category"
            value={editor.form.category}
            onChange={(value) => onPatch({ category: value })}
            options={categories}
            emptyLabel="Choose a category"
            colors={colors}
          />
          <FrequencyField
            id="starter-template-frequency"
            value={editor.form.frequency}
            onChange={(value) => onPatch({ frequency: value })}
            field={field}
            labelStyle={labelStyle}
          />
          <label style={labelStyle} htmlFor="starter-template-description">Description</label>
          <textarea id="starter-template-description" value={editor.form.description} onChange={(event) => onPatch({ description: event.target.value })} style={{ ...field, minHeight: '6rem' }} />
          <h4 style={{ marginBottom: '0.35rem' }}>Tasks</h4>
          <p style={{ marginTop: 0, color: colors.textSecondary, lineHeight: 1.45 }}>
            Task changes stay on this template until you save it.
          </p>
          {tasks.length === 0 ? <p style={{ color: colors.text }}>No tasks yet.</p> : null}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {tasks.map((task, index) => (
              <div key={task.id || index} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => onEditTask(index)}
                  aria-label={`Edit task ${task.title || index + 1}`}
                  style={{ ...quietButton(colors), flex: '1 1 auto', textAlign: 'left', borderRadius: '0.9rem' }}
                >
                  <span style={{ display: 'block', fontWeight: 650 }}>{task.title || 'Untitled task'}</span>
                  <span style={{ display: 'block', marginTop: '0.15rem', color: colors.textSecondary, fontSize: '0.9rem' }}>
                    {[task.category, task.frequency].filter(Boolean).join(' · ') || 'Add details'}
                  </span>
                </button>
                <button type="button" aria-label={`Move ${task.title || 'task'} up`} disabled={index === 0 || busy} onClick={() => onReorder(index, -1)} style={iconButton(colors)}>
                  Up
                </button>
                <button type="button" aria-label={`Move ${task.title || 'task'} down`} disabled={index === tasks.length - 1 || busy} onClick={() => onReorder(index, 1)} style={iconButton(colors)}>
                  Down
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={onAddTask} style={{ ...quietButton(colors), marginTop: '0.75rem' }}>Add task</button>
          {error ? <p role="alert" style={{ color: '#b91c1c' }}>{error}</p> : null}
        </div>
        <div className="app-modal-actions" style={{ background: colors.surface }}>
          <button type="button" onClick={onSave} disabled={busy} style={primaryButton(colors)}>Save</button>
          {editor.id ? (
            <button type="button" onClick={onDelete} disabled={busy} style={quietButton(colors)}>Delete</button>
          ) : null}
          <button type="button" onClick={onClose} style={quietButton(colors)}>Close</button>
        </div>
      </div>
    </ModalLayer>
  );
}

function TaskDraftEditor({
  draft,
  colors,
  field,
  labelStyle,
  categories,
  error,
  onPatch,
  onSave,
  onRemove,
  onClose,
}) {
  return (
    <ModalLayer style={{ ...sheetOverlayStyle, zIndex: 1150 }} onClick={onClose}>
      <div
        className="app-modal-card starter-template-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="starter-task-editor-title"
        onClick={(event) => event.stopPropagation()}
        style={{ background: colors.surface, color: colors.text, width: 'min(100%, 40rem)', borderRadius: '1rem', boxSizing: 'border-box' }}
      >
        <div className="app-modal-header" style={{ background: colors.surface }}>
          <h3 id="starter-task-editor-title">{draft.index == null ? 'New task' : 'Edit task'}</h3>
        </div>
        <div className="starter-template-sheet-body">
          <label style={labelStyle} htmlFor="starter-task-name">Name</label>
          <input id="starter-task-name" value={draft.form.title} onChange={(event) => onPatch({ title: event.target.value })} style={field} />
          <SearchableCategorySelect
            id="starter-task-category"
            label="Category"
            value={draft.form.category}
            onChange={(value) => onPatch({ category: value })}
            options={categories}
            emptyLabel="Choose a category"
            colors={colors}
          />
          <FrequencyField
            id="starter-task-frequency"
            value={draft.form.frequency}
            onChange={(value) => onPatch({ frequency: value })}
            field={field}
            labelStyle={labelStyle}
          />
          <label style={labelStyle} htmlFor="starter-task-description">Description</label>
          <textarea id="starter-task-description" value={draft.form.description} onChange={(event) => onPatch({ description: event.target.value })} style={{ ...field, minHeight: '6rem' }} />
          {error ? <p role="alert" style={{ color: '#b91c1c' }}>{error}</p> : null}
        </div>
        <div className="app-modal-actions" style={{ background: colors.surface }}>
          <button type="button" onClick={onSave} style={primaryButton(colors)}>Save</button>
          {draft.index != null ? (
            <button type="button" onClick={onRemove} style={quietButton(colors)}>Remove</button>
          ) : null}
          <button type="button" onClick={onClose} style={quietButton(colors)}>Close</button>
        </div>
      </div>
    </ModalLayer>
  );
}

function primaryButton(colors) {
  return {
    minHeight: '44px',
    padding: '0.65rem 1rem',
    borderRadius: '999px',
    border: 'none',
    background: colors.text,
    color: colors.background || '#fff',
    fontWeight: 650,
  };
}

function quietButton(colors) {
  return {
    minHeight: '44px',
    padding: '0.65rem 1rem',
    borderRadius: '999px',
    border: `1px solid ${colors.border}`,
    background: 'transparent',
    color: colors.text,
    fontWeight: 650,
  };
}

function iconButton(colors) {
  return {
    ...quietButton(colors),
    flex: '0 0 auto',
    minWidth: '44px',
    padding: '0.55rem 0.7rem',
  };
}

function segmentButton(colors, selected) {
  return {
    minHeight: '44px',
    padding: '0.55rem 0.9rem',
    borderRadius: '999px',
    border: `1px solid ${selected ? colors.text : colors.border}`,
    background: selected ? colors.text : 'transparent',
    color: selected ? (colors.background || '#fff') : colors.textSecondary,
    fontWeight: 650,
  };
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 1100,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  padding: '0.75rem',
};

const sheetOverlayStyle = {
  ...overlayStyle,
  alignItems: 'center',
  paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))',
  paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
  paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
  paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
};
